import React, { useEffect, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { get } from './httpClient';
import ImageUploader from './ImageUploader';
import RichTextEditor, { safeParseBody } from './RichTextEditor';
import { renderEditorDataToHTML } from './Editor';
import PosterWidgetEditor from './components/PosterWidgetEditor';
import { PropertyTypeIcon } from './propertyTypeIcons';
import { extractYouTubeId, youTubeThumbnailUrl } from './youtube';

// Default value for a poster-type layout property — mirrors the reusable Poster Component's config.
const DEFAULT_POSTER = {
  backgroundType: 'image',
  background: '',
  backgroundVertical: '',
  heading: '',
  subheading: '',
  overlayColor: '#ffffff',
  textShadeColor: '#000000',
};

// Only pick known poster fields out of a stored value. Two components in the same layout can
// end up sharing a name (e.g. a leftover 'widget' property also called "poster"), in which
// case a blind spread would drag an unrelated widgetId into the poster payload and the
// backend would then misclassify the whole value as a component reference, silently dropping
// the poster content. Whitelisting here keeps this editor's output well-formed regardless.
function pickPosterFields(src) {
  const s = (src && typeof src === 'object') ? src : {};
  return {
    backgroundType: s.backgroundType ?? DEFAULT_POSTER.backgroundType,
    background: s.background ?? DEFAULT_POSTER.background,
    backgroundVertical: s.backgroundVertical ?? DEFAULT_POSTER.backgroundVertical,
    heading: s.heading ?? DEFAULT_POSTER.heading,
    subheading: s.subheading ?? DEFAULT_POSTER.subheading,
    overlayColor: s.overlayColor ?? DEFAULT_POSTER.overlayColor,
    textShadeColor: s.textShadeColor ?? DEFAULT_POSTER.textShadeColor,
  };
}

// Settings for a gallery-type layout property (multi-image carousel).
// behaves the same way as an embedded gallery component.
const DEFAULT_GALLERY_META = {
  autoPlay: true,
  showNav: false,
  showFullScreenButton: true,
  showThumbnails: false,
  showImageTitles: true,
  slideIntervalSeconds: 7,
};

function normalizeGalleryMeta(m) {
  const src = (m && typeof m === 'object') ? m : {};
  return {
    autoPlay: src.autoPlay !== undefined ? !!src.autoPlay : DEFAULT_GALLERY_META.autoPlay,
    showNav: src.showNav !== undefined ? !!src.showNav : DEFAULT_GALLERY_META.showNav,
    showFullScreenButton: src.showFullScreenButton !== undefined ? !!src.showFullScreenButton : DEFAULT_GALLERY_META.showFullScreenButton,
    showThumbnails: src.showThumbnails !== undefined ? !!src.showThumbnails : DEFAULT_GALLERY_META.showThumbnails,
    showImageTitles: src.showImageTitles !== undefined ? !!src.showImageTitles : DEFAULT_GALLERY_META.showImageTitles,
    slideIntervalSeconds: Number.isFinite(Number(src.slideIntervalSeconds)) && Number(src.slideIntervalSeconds) > 0
      ? Number(src.slideIntervalSeconds) : DEFAULT_GALLERY_META.slideIntervalSeconds,
  };
}

export function normalizeProperty(p) {
  const rawType = (p?.type || '').toLowerCase();
  const type = ['gallery', 'text', 'line', 'date', 'widget', 'poster', 'catalog', 'breadcrumbs'].includes(rawType) ? rawType : 'text';
  let meta = null;
  if (type === 'line') {
    const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
    const fs = String(m.fontSize ?? '').toLowerCase();
    const allowedFontSizes = ['heading', 'sub-heading', 'text'];
    meta = {
      prefix: String(m.prefix ?? ''),
      suffix: String(m.suffix ?? ''),
      fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
    };
  } else if (type === 'widget') {
    const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
    meta = { widgetId: m.widgetId ? String(m.widgetId) : null };
  } else if (type === 'text') {
    const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
    const cut = String(m.cut ?? '').toLowerCase();
    meta = { cut: ['none', 'small', 'medium'].includes(cut) ? cut : 'none' };
  } else if (type === 'gallery') {
    meta = normalizeGalleryMeta(p && p.meta);
  }
  return {
    name: String(p?.name ?? ''),
    type,
    required: !!p?.required,
    show_name: p?.show_name === undefined ? true : !!p.show_name,
    as_title: !!p?.as_title,
    as_subtitle: !!p?.as_subtitle,
    meta,
    alignment: ['left', 'center', 'right'].includes(p?.alignment) ? p.alignment : 'left',
  };
}

export function normalizeRows(rows) {
  const rowsArr = Array.isArray(rows) ? rows : [];
  return rowsArr.map((row) => ({
    columns: Array.isArray(row?.columns)
      ? row.columns.map((col) => ({
          width: Number.isFinite(Number(col?.width)) && Number(col.width) > 0 ? Number(col.width) : 100,
          components: Array.isArray(col?.components) ? col.components.map(normalizeProperty) : [],
        }))
      : [],
  }));
}

export function allProperties(rows) {
  const props = [];
  for (const row of rows || []) {
    for (const col of row.columns || []) {
      for (const p of col.components || []) props.push(p);
    }
  }
  return props;
}

// Prepares raw prop values for submission: renders editorjs bodies to HTML (the server can only
// render markdown itself) and defaults empty date fields to today. Call this at submit time,
// mirroring how each page's own rich-text Content field is rendered right before saving.
export function finalizeTemplateProps(rawProps, rows, { captionFallback } = {}) {
  const finalProps = { ...(rawProps || {}) };
  const today = new Date().toISOString().slice(0, 10);
  for (const p of allProperties(normalizeRows(rows))) {
    if (p.as_title && captionFallback !== undefined && !(p.name in finalProps)) {
      finalProps[p.name] = (captionFallback || '').trim();
    }
    if (p.type === 'date' && !finalProps[p.name]) {
      finalProps[p.name] = today;
    }
    if (p.type === 'poster' && finalProps[p.name] !== undefined) {
      const val = finalProps[p.name];
      if (val && typeof val === 'object') {
        finalProps[p.name] = pickPosterFields(val);
      } else {
        delete finalProps[p.name];
      }
    }
    if (p.type === 'text' && finalProps[p.name] !== undefined) {
      const val = finalProps[p.name];
      if (val && typeof val === 'object' && 'body' in val) {
        if (!val.type || val.type === 'editorjs') {
          const bodyObj = safeParseBody(val.body);
          finalProps[p.name] = { body: JSON.stringify(bodyObj), rendered: renderEditorDataToHTML(bodyObj), type: 'editorjs' };
        } else {
          finalProps[p.name] = { body: typeof val.body === 'string' ? val.body : '', rendered: '', type: 'markdown' };
        }
      }
    }
    if (p.type === 'catalog' && !(p.name in finalProps)) {
      finalProps[p.name] = { itemLayoutId: null, elementsInARow: 3 };
    }
  }
  return finalProps;
}

// Renders an editable input for every field defined by a layout template's rows, bound to a
// {propertyName: value} map. Used both by GalleryItem pages (structure inherited from the parent
// Catalog/Gallery) and by any page's own selected layout.
function TemplatePropertiesEditor({ websiteId, rows, props, onChange }) {
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [availableLayouts, setAvailableLayouts] = useState([]);
  const structureRows = normalizeRows(rows);
  const propsMap = props || {};

  useEffect(() => {
    if (!websiteId) return;
    let mounted = true;
    get(`websites/${websiteId}/widgets`)
      .then((resp) => { if (mounted) setAvailableWidgets(resp?.widgets ?? []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [websiteId]);

  useEffect(() => {
    if (!websiteId) return;
    let mounted = true;
    get(`websites/${websiteId}/layouts`)
      .then((resp) => { if (mounted) setAvailableLayouts(resp?.layouts ?? []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [websiteId]);

  const handleChangeProp = (name, value) => onChange({ ...propsMap, [name]: value });
  const handleClearProp = (name) => {
    const next = { ...propsMap };
    delete next[name];
    onChange(next);
  };

  const renderPropertyInput = (p) => {
    const key = p.name;
    if (!key) return null;

    const labelEl = (
      <Form.Label className="item-prop-label">
        <span className="item-prop-label-name">
          {p.name}
          {p.required && <span className="item-prop-required" title="Required">*</span>}
        </span>
        <span className={`item-prop-type-badge item-prop-type-badge--${p.type}`}>
          <PropertyTypeIcon type={p.type} />
          {p.type}
        </span>
      </Form.Label>
    );

    switch (p.type) {
      case 'gallery': {
        // Legacy 'image' components stored a single URL string; show it as a
        // one-item gallery instead of silently hiding it from the editor.
        const rawVal = propsMap[key];
        const items = Array.isArray(rawVal) ? rawVal : (typeof rawVal === 'string' && rawVal ? [{ type: 'image', src: rawVal, title: '' }] : []);
        const addItem = (item) => handleChangeProp(key, [...items, item]);
        const updateItem = (itemIdx, field, val) => {
          handleChangeProp(key, items.map((im, j) => (j === itemIdx ? { ...im, [field]: val } : im)));
        };
        const removeItem = (itemIdx) => handleChangeProp(key, items.filter((_, j) => j !== itemIdx));

        let videoUrlInput;
        const addVideo = () => {
          const url = (videoUrlInput?.value || '').trim();
          if (!url) return;
          if (!extractYouTubeId(url)) {
            toast.error("That doesn't look like a YouTube URL.");
            return;
          }
          addItem({ type: 'youtube', src: url, title: '' });
          if (videoUrlInput) videoUrlInput.value = '';
        };

        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
              <span className="component-item-field-label" style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--green)' }}>
                Media ({items.length})
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ImageUploader
                  websiteId={websiteId}
                  onUploaded={(resp) => {
                    const url = resp?.url || resp?.data?.url || resp?.file?.url || '';
                    if (!url) return;
                    addItem({ type: 'image', src: url, title: '' });
                  }}
                  label="Upload image"
                />
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Paste YouTube URL"
                  style={{ width: 170 }}
                  ref={(el) => { videoUrlInput = el; }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
                />
                <Button type="button" size="sm" className="primary-button m-0" onClick={addVideo}>Add video</Button>
              </div>
            </div>

            {items.length === 0 && (
              <p className="muted-hint mb-0">No images or videos added yet</p>
            )}

            <div className="comp-img-list">
              {items.map((item, itemIdx) => {
                const isVideo = item.type === 'youtube';
                const thumbSrc = isVideo ? youTubeThumbnailUrl(item.src) : item.src;
                return (
                  <div key={itemIdx} className="comp-img-row">
                    <div className="comp-img-thumb-wrap">
                      {thumbSrc ? (
                        <img src={thumbSrc} alt={item.title || `${isVideo ? 'video' : 'image'}-${itemIdx}`} className="comp-img-thumb" />
                      ) : (
                        <div className="comp-img-thumb--broken" title={item.src}>Invalid link</div>
                      )}
                      {isVideo && thumbSrc && <span className="comp-img-video-badge" aria-hidden="true">▶</span>}
                    </div>
                    <Form.Control
                      type="text"
                      value={item.title || ''}
                      onChange={(e) => updateItem(itemIdx, 'title', e.target.value)}
                      placeholder={isVideo ? 'Video title (optional)' : 'Image title (optional)'}
                      size="sm"
                    />
                    <button
                      type="button"
                      className="comp-img-remove"
                      onClick={() => removeItem(itemIdx)}
                      title={`Remove ${isVideo ? 'video' : 'image'}`}
                    >×</button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      case 'poster': {
        const item = pickPosterFields(propsMap[key]);
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <PosterWidgetEditor
              item={item}
              websiteId={websiteId}
              onChange={(next) => handleChangeProp(key, next)}
            />
          </div>
        );
      }
      case 'text': {
        const rawVal = propsMap[key];
        let editorValue, editorType;
        if (rawVal && typeof rawVal === 'object' && 'body' in rawVal) {
          editorType = rawVal.type || 'editorjs';
          editorValue = editorType === 'editorjs' ? safeParseBody(rawVal.body) : (rawVal.body || '');
        } else if (typeof rawVal === 'string' && rawVal) {
          editorType = 'editorjs';
          editorValue = { blocks: [{ type: 'paragraph', data: { text: rawVal } }] };
        } else {
          editorType = 'editorjs';
          editorValue = { blocks: [] };
        }
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <RichTextEditor
              key={`${key}-${editorType}`}
              value={editorValue}
              type={editorType}
              onChange={(data) => handleChangeProp(key, { body: data, type: editorType })}
              onTypeChange={(newType) => handleChangeProp(key, { body: newType === 'editorjs' ? { blocks: [] } : '', type: newType })}
              websiteId={websiteId}
            />
          </div>
        );
      }
      case 'breadcrumbs': {
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <div className="item-prop-hint">
              Shows this page's breadcrumb trail automatically — no settings, just drag it to
              where you want the trail to appear.
            </div>
          </div>
        );
      }
      case 'catalog': {
        // Always renders whatever children this page actually has — item layout and
        // elements-in-a-row are this page's own values, not the (possibly shared) layout's,
        // so the same layout can host a catalog with different settings on different pages.
        const value = (propsMap[key] && typeof propsMap[key] === 'object') ? propsMap[key] : {};
        const itemLayoutId = value.itemLayoutId || '';
        const elementsInARow = value.elementsInARow ?? 3;
        const update = (patch) => handleChangeProp(key, { itemLayoutId: value.itemLayoutId ?? null, elementsInARow, ...patch });
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <div className="mb-2">
              <div className="component-item-field-label">Item layout</div>
              <Form.Select
                value={itemLayoutId}
                onChange={(e) => update({ itemLayoutId: e.target.value || null })}
              >
                <option value="">— Children pick their own —</option>
                {availableLayouts.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="mb-2">
              <div className="component-item-field-label">Elements in a row</div>
              <Form.Control
                type="number"
                style={{ width: 90 }}
                min={1}
                value={elementsInARow}
                onChange={(e) => update({ elementsInARow: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              />
            </div>
            {itemLayoutId && (
              <div className="item-prop-hint">Child pages use this layout and can't pick their own.</div>
            )}
          </div>
        );
      }
      case 'widget': {
        const publicWidgets = availableWidgets;
        const isOverridden = key in propsMap;
        const selectedId = isOverridden ? (propsMap[key]?.widgetId || '') : (p.meta?.widgetId || '');
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <Form.Select
              value={selectedId}
              onChange={(e) => {
                const widgetId = e.target.value;
                if (!widgetId) {
                  handleClearProp(key);
                } else {
                  handleChangeProp(key, { widgetId });
                }
              }}
            >
              <option value="">Select a widget…</option>
              {publicWidgets.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
              ))}
            </Form.Select>
            {!isOverridden && p.meta?.widgetId && (
              <div className="item-prop-hint">default from layout</div>
            )}
          </div>
        );
      }
      case 'date': {
        const today = new Date().toISOString().slice(0, 10);
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <Form.Control
              type="date"
              value={propsMap[key] || today}
              onChange={(e) => handleChangeProp(key, e.target.value)}
            />
          </div>
        );
      }
      case 'line':
      default: {
        const prefix = p.meta?.prefix || '';
        const suffix = p.meta?.suffix || '';
        const hasAffix = prefix || suffix;
        return (
          <div key={key} className={`item-prop item-prop--${p.type}`}>
            {labelEl}
            <Form.Control
              type="text"
              value={propsMap[key] || ''}
              onChange={(e) => handleChangeProp(key, e.target.value)}
              placeholder={p.name}
            />
            {hasAffix && (
              <div className="item-prop-hint">
                {prefix && <span>prefix: <em>"{prefix}"</em></span>}
                {prefix && suffix && <span> · </span>}
                {suffix && <span>suffix: <em>"{suffix}"</em></span>}
              </div>
            )}
          </div>
        );
      }
    }
  };

  if (allProperties(structureRows).length === 0) return null;

  return (
    <>
      {structureRows.map((row, rIdx) => {
        const visibleColumns = row.columns
          .map((col, idx) => ({ col, idx }))
          .filter(({ col }) => col.components.length > 0);
        if (visibleColumns.length === 0) return null;
        return (
        <div key={rIdx} className="item-props-row">
          {structureRows.length > 1 && (
            <div className="item-props-row-label">Row {rIdx + 1}</div>
          )}
          <div className={`item-props-columns${visibleColumns.length > 1 ? ' item-props-columns--multi' : ''}`}>
            {visibleColumns.map(({ col, idx }) => (
              <div key={idx} className="item-props-column">
                {row.columns.length > 1 && (
                  <div className="item-props-column-label">Column {idx + 1}</div>
                )}
                <div className="item-props-grid">
                  {col.components.map((p) => renderPropertyInput(p))}
                </div>
              </div>
            ))}
          </div>
        </div>
        );
      })}
    </>
  );
}

export default TemplatePropertiesEditor;
